import { ChecklistTemplate } from './types';

/**
 * Plantilla de revisión para Hidrantes (columna seca, columna húmeda y arqueta).
 *
 * Referencias normativas:
 *   - RD 513/2017 (RIPCI), Anexo II, Tabla I.
 *   - UNE 23405 / UNE 23406 (hidrantes de columna).
 *   - UNE-EN 14339 (hidrantes bajo nivel del suelo - arqueta).
 *   - UNE-EN 14384 (hidrantes de columna).
 *
 * Periodicidad:
 *   T = Trimestral
 *   A = Anual
 */
export const HIDRANTES_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'hidrantes',
  titulo: 'Sistema de Hidrantes',
  protocolos: [
    {
      id: 'inventario',
      titulo: '1. Inventario y distribución',
      cabecera: [
        { key: 'num_hidrantes', label: 'Nº total de hidrantes', tipo: 'number' },
        { key: 'num_columna_seca', label: 'Nº columna seca', tipo: 'number' },
        { key: 'num_columna_humeda', label: 'Nº columna húmeda', tipo: 'number' },
        { key: 'num_arqueta', label: 'Nº de arqueta', tipo: 'number' },
        { key: 'tipo_red', label: 'Tipo de red (anillada / ramificada)' },
        { key: 'presion_estatica_red', label: 'Presión estática red (bar)' },
      ],
      preguntas: [
        { id: 'IN.01', texto: '¿Coincide el inventario físico con planos / listado del cliente?', periodicidad: ['A'] },
        { id: 'IN.02', texto: '¿La separación entre hidrantes (≤ 100 m zonas urbanas / ≤ 200 m industriales) cumple normativa?', periodicidad: ['A'] },
        { id: 'IN.03', texto: '¿Existen hidrantes próximos a riesgos especiales del edificio?', periodicidad: ['A'] },
        { id: 'IN.04', texto: '¿Cada hidrante dispone de etiqueta de revisión por empresa mantenedora visible y legible?', periodicidad: ['T','A'], unidades: true },
      ],
    },
    {
      id: 'accesibilidad',
      titulo: '2. Accesibilidad y señalización',
      preguntas: [
        { id: 'AC.01', texto: '¿El hidrante está libre de obstáculos (vehículos, vegetación, contenedores, mobiliario urbano)?', periodicidad: ['T','A'], unidades: true },
        { id: 'AC.02', texto: '¿Está correctamente señalizado conforme UNE 23033?', periodicidad: ['T','A'], unidades: true },
        { id: 'AC.03', texto: '¿La señalización es visible desde la vía pública y en horario nocturno?', periodicidad: ['A'], unidades: true },
        { id: 'AC.04', texto: '¿El acceso del vehículo de bomberos al hidrante es viable?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'estado_fisico',
      titulo: '3. Estado físico del hidrante',
      preguntas: [
        { id: 'EF.01', texto: '¿La tapa de arqueta o el cuerpo de la columna están en buen estado (sin oxidación, golpes ni deformaciones)?', periodicidad: ['T','A'], unidades: true },
        { id: 'EF.02', texto: '¿Las bocas de salida (70 mm / 100 mm) y los racores Barcelona/Storz están sin daños?', periodicidad: ['T','A'], unidades: true },
        { id: 'EF.03', texto: '¿Los tapones y cadenas están presentes y en buen estado?', periodicidad: ['T','A'], unidades: true },
        { id: 'EF.04', texto: '¿La pintura y protección anticorrosión están en buen estado?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'maniobra',
      titulo: '4. Maniobra y mantenimiento mecánico',
      cabecera: [
        { key: 'fecha_cambio_juntas', label: 'Fecha último cambio de juntas', tipo: 'date' },
      ],
      preguntas: [
        { id: 'MN.01', texto: '¿La maniobra de apertura/cierre (volante o llave) gira sin atascos ni esfuerzo anómalo?', periodicidad: ['T','A'], unidades: true },
        { id: 'MN.02', texto: '¿Se ha engrasado el husillo y mecanismos de maniobra?', periodicidad: ['A'], unidades: true },
        { id: 'MN.03', texto: '¿Se han revisado las juntas y sustituido las deterioradas?', periodicidad: ['A'], unidades: true },
        { id: 'MN.04', texto: 'En hidrantes de columna seca: ¿el drenaje funciona correctamente al cerrar (protección anti-heladas)?', periodicidad: ['A'], unidades: true },
        { id: 'MN.05', texto: '¿No se observan fugas externas en cuerpo, racores ni válvula?', periodicidad: ['T','A'], unidades: true },
      ],
    },
    {
      id: 'pruebas',
      titulo: '5. Pruebas hidráulicas',
      cabecera: [
        { key: 'presion_dinamica', label: 'Presión dinámica medida (bar)' },
        { key: 'caudal_medido', label: 'Caudal medido (l/min)' },
      ],
      preguntas: [
        { id: 'PR.01', texto: '¿Se ha realizado prueba de presión dinámica abriendo el hidrante (≥ 5 bar a caudal de 1.000 l/min)?', periodicidad: ['A'], unidades: true },
        { id: 'PR.02', texto: '¿Se ha verificado la estanqueidad de la válvula tras la prueba?', periodicidad: ['A'], unidades: true },
        { id: 'PR.03', texto: '¿El caudal y presión cumplen los requisitos del proyecto de instalación?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'red_general',
      titulo: '6. Red general y abastecimiento',
      preguntas: [
        { id: 'RG.01', texto: '¿La red de tubería (visible) presenta buen estado, sin corrosión ni fugas?', periodicidad: ['A'] },
        { id: 'RG.02', texto: '¿Las válvulas de seccionamiento están abiertas, accesibles e identificadas?', periodicidad: ['A'] },
        { id: 'RG.03', texto: '¿El depósito o aljibe que alimenta la red dispone de la capacidad mínima exigida?', periodicidad: ['A'] },
        { id: 'RG.04', texto: '¿La red está protegida frente a heladas en zonas expuestas?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'documentacion',
      titulo: '7. Documentación y registros',
      preguntas: [
        { id: 'DC.01', texto: '¿Existe acta firmada de la última revisión anual realizada por empresa mantenedora habilitada?', periodicidad: ['A'] },
        { id: 'DC.02', texto: '¿Está actualizado el inventario con marca, modelo, ubicación, tipo, diámetro de salida y fecha de cambio de juntas?', periodicidad: ['A'] },
        { id: 'DC.03', texto: '¿Se ha entregado al titular el informe con anomalías detectadas y acciones correctoras?', periodicidad: ['A'] },
      ],
    },
  ],
};
